import { useState } from "react";
import { Bell, Palette } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Button } from "./ui/button";
import { Switch } from "./ui/switch";
import { Label } from "./ui/label";
import { useTheme } from "next-themes";
import { AuthRepository } from "../infrastructure/repositories/AuthRepository";

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "./ui/alertDialog";

import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "./ui/dialog";

import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";

import { UserRepository } from "../infrastructure/repositories/UserRepository";
import { Email } from "../domain/valueObjects/Email";
import { Password } from "../domain/valueObjects/Password";
import { secureStorage } from "@/utils/secureStorage";


export function SettingsContent() {

  const [isDeleting, setIsDeleting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [showDeleteError, setShowDeleteError] = useState(false);
  const [deleteErrorMessage, setDeleteErrorMessage] = useState("");
  const [deletePassword, setDeletePassword] = useState("");
  const { theme, setTheme } = useTheme();


  // DELETE ACCOUNT HANDLER
  const handleConfirmDelete = async () => {
    const email =
        secureStorage.get<string>("current_user_email") ?? "";
    const userId = localStorage.getItem("current_user_swimmer_id");

    if (!email || !userId) {
      setDeleteErrorMessage("Session expired. Please log in again.");
      setShowDeleteError(true);
      return;
    }

    if (!deletePassword.trim()) {
      setDeleteErrorMessage("Please enter your password.");
      setShowDeleteError(true);
      return;
    }

    const authRepo = new AuthRepository();
    const userRepo = new UserRepository();

    try {
      setIsDeleting(true);

      await authRepo.login(new Email(email), new Password(deletePassword));

      await userRepo.deleteWithPassword(userId, deletePassword);

      setShowSuccess(true);
      localStorage.clear();

      setTimeout(() => {
        window.location.href = "/login";
      }, 2000);

    } catch (err) {
      console.error("Delete failed:", err);

      setDeleteErrorMessage("Incorrect password or delete failed.");
      setShowDeleteError(true);

    } finally {
      setIsDeleting(false);
      setDeletePassword("");
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold">Settings</h1>
        <p className="text-muted-foreground">Manage your account preferences and application settings</p>
      </div>

      
      {/* Notification Settings */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center">
            <Bell className="mr-2 h-5 w-5" />
            Notifications
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <Label htmlFor="email-notifications">Email Notifications</Label>
              <p className="text-sm text-muted-foreground">Receive notifications via email</p>
            </div>
            <Switch id="email-notifications" defaultChecked />
          </div>
          <div className="flex items-center justify-between">
            <div>
              <Label htmlFor="push-notifications">Push Notifications</Label>
              <p className="text-sm text-muted-foreground">Receive push notifications on your device</p>
            </div>
            <Switch id="push-notifications" defaultChecked />
          </div>
          <div className="flex items-center justify-between">
            <div>
              <Label htmlFor="training-reminders">Training Reminders</Label>
              <p className="text-sm text-muted-foreground">Get reminded about upcoming training sessions</p>
            </div>
            <Switch id="training-reminders" defaultChecked />
          </div>
          <div className="flex items-center justify-between">
            <div>
              <Label htmlFor="achievement-alerts">Achievement Alerts</Label>
              <p className="text-sm text-muted-foreground">Get notified when you achieve new milestones</p>
            </div>
            <Switch id="achievement-alerts" defaultChecked />
          </div>
        </CardContent>
      </Card>

      {/* Privacy & Security */}
      {/* <Card>
        <CardHeader>
          <CardTitle className="flex items-center">
            <Shield className="mr-2 h-5 w-5" />
            Privacy & Security
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <Label htmlFor="profile-visibility">Profile Visibility</Label>
              <p className="text-sm text-muted-foreground">Make your profile visible to other users</p>
            </div>
            <Switch id="profile-visibility" defaultChecked />
          </div>
          <div className="flex items-center justify-between">
            <div>
              <Label htmlFor="data-sharing">Data Sharing</Label>
              <p className="text-sm text-muted-foreground">Allow sharing of anonymized data for research</p>
            </div>
            <Switch id="data-sharing" />
          </div>
          <div className="flex items-center justify-between">
            <div>
              <Label htmlFor="two-factor">Two-Factor Authentication</Label>
              <p className="text-sm text-muted-foreground">Add an extra layer of security to your account</p>
            </div>
            <Switch id="two-factor" />
          </div>
        </CardContent>
      </Card> */}

      {/* Appearance Settings */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center">
            <Palette className="mr-2 h-5 w-5" />
            Appearance
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
          <Label htmlFor="theme">Theme</Label>
          <Select value={theme} onValueChange={(value) => setTheme(value)}>
            <SelectTrigger>
              <SelectValue placeholder="Select theme" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="light">Light</SelectItem>
              <SelectItem value="dark">Dark</SelectItem>
              <SelectItem value="system">System</SelectItem>
            </SelectContent>
          </Select>
        </div>

          {/* <div>
            <Label htmlFor="language">Language</Label>
            <Select defaultValue="en">
              <SelectTrigger>
                <SelectValue placeholder="Select language" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="en">English</SelectItem>
                <SelectItem value="es">Spanish</SelectItem>
                <SelectItem value="fr">French</SelectItem>
                <SelectItem value="de">German</SelectItem>
              </SelectContent>
            </Select>
          </div> */}
          {/* <div>
            <Label htmlFor="timezone">Timezone</Label>
            <Select defaultValue="pst">
              <SelectTrigger>
                <SelectValue placeholder="Select timezone" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="pst">Pacific Standard Time</SelectItem>
                <SelectItem value="est">Eastern Standard Time</SelectItem>
                <SelectItem value="cst">Central Standard Time</SelectItem>
                <SelectItem value="mst">Mountain Standard Time</SelectItem>
              </SelectContent>
            </Select>
          </div> */}
        </CardContent>
      </Card>

      {/* Application Settings */}
      {/* <Card>
        <CardHeader>
          <CardTitle className="flex items-center">
            <Settings className="mr-2 h-5 w-5" />
            Application Settings
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <Label htmlFor="auto-save">Auto-save Progress</Label>
              <p className="text-sm text-muted-foreground">Automatically save your training progress</p>
            </div>
            <Switch id="auto-save" defaultChecked />
          </div>
          <div className="flex items-center justify-between">
            <div>
              <Label htmlFor="analytics">Analytics</Label>
              <p className="text-sm text-muted-foreground">Help improve the app by sharing usage analytics</p>
            </div>
            <Switch id="analytics" defaultChecked />
          </div>
          <div className="flex items-center justify-between">
            <div>
              <Label htmlFor="crash-reports">Crash Reports</Label>
              <p className="text-sm text-muted-foreground">Send crash reports to help fix bugs</p>
            </div>
            <Switch id="crash-reports" defaultChecked />
          </div>
        </CardContent>
      </Card> */}

      {/* Data Management */}
       <Card>
          <CardHeader>
            <CardTitle>Data Management</CardTitle>
          </CardHeader>

        <CardContent className="space-y-4">

          {/* Delete Account */}
          <div className="flex items-center justify-between">
            <div>
              <h4 className="font-medium">Delete Account</h4>
              <p className="text-sm text-muted-foreground">
                Permanently delete your account and all data
              </p>
            </div>

            {/* DELETE PASSWORD PROMPT DIALOG */}
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button variant="destructive" disabled={isDeleting}>
                  {isDeleting ? "Deleting..." : "Delete Account"}
                </Button>
              </AlertDialogTrigger>

              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Enter your password</AlertDialogTitle>
                  <AlertDialogDescription>
                    Please confirm your password to permanently delete your account.
                  </AlertDialogDescription>
                </AlertDialogHeader>

                {/* PASSWORD INPUT */}
                <div className="mt-4">
                  <input
                    type="password"
                    className="border rounded p-2 w-full"
                    placeholder="Enter your password"
                    value={deletePassword}
                    onChange={(e) => setDeletePassword(e.target.value)}
                  />
                </div>

                <AlertDialogFooter>
                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                  <AlertDialogAction onClick={handleConfirmDelete}>
                    Yes, Delete
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>

            </AlertDialog>
          </div>
        </CardContent>
      </Card>


      {/* Success Dialog */}
      <Dialog open={showSuccess} onOpenChange={setShowSuccess}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Account Deleted</DialogTitle>
            <DialogDescription>
              Your account has been successfully deleted. Redirecting to login...
            </DialogDescription>
          </DialogHeader>
        </DialogContent>
      </Dialog>

    {/*   ERROR DIALOG  */}
    
      <Dialog open={showDeleteError} onOpenChange={setShowDeleteError}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Error</DialogTitle>
            <DialogDescription className="text-red-600">
              {deleteErrorMessage}
            </DialogDescription>
          </DialogHeader>

          <div className="flex justify-end">
            <Button onClick={() => setShowDeleteError(false)}>
              Close
            </Button>
          </div>
        </DialogContent>
      </Dialog>

    </div>
  );
}
